import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CreditCard,
  ExternalLink,
  Loader2,
  LogOut,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSubscription } from "@/hooks/useSubscription";
import { UpsellContent } from "@/components/UpsellContent";
import { PaymentVerificationPanel } from "@/components/account/PaymentVerificationPanel";
import { getStripeEnvironment } from "@/lib/stripe";
import { isProjectOwner } from "@/lib/owner";
import { PRICES, type PriceId } from "@/lib/pricing";
import { usePreferences } from "@/hooks/usePreferences";
import type { DietPreference, UnitSystem } from "@/lib/preferences";
import { dietLabel } from "@/lib/preferences";
import { MEAL_SLOTS, type MealSlot } from "@/lib/planner";
import {
  getUserShoppingPrefs,
  saveDefaultMealSlots,
} from "@/lib/userShoppingPrefs";

/**
 * Manage Subscription panel.
 *
 * Surfaces everything a paying / trialing user needs in one place:
 *   - Plan + billing status (with trial countdown when relevant)
 *   - Switch plan during trial (mounts UpsellContent in a sheet)
 *   - Open Stripe billing portal for invoices + payment method
 *   - In-app cancel (sets cancel_at_period_end via edge function)
 *   - Sign out
 *   - Permanent account deletion (typed confirmation)
 *
 * Non-subscribers landing here see a friendly nudge to upgrade rather than a
 * broken-looking empty state.
 */
const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const planLabel = (priceId: string | null): string => {
  if (!priceId) return "Premium";
  const price = PRICES[priceId as PriceId];
  if (!price) return "Premium";
  const tier = priceId.startsWith("founding") ? "Founding" : "Standard";
  return `${tier} · ${price.label}${price.subLabel}`;
};

const Account = () => {
  const navigate = useNavigate();
  const { email, subscription, isActive, inTrial, daysLeftInTrial, loading, refresh } =
    useSubscription();
  const [authChecked, setAuthChecked] = useState(false);

  // Local UI state.
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Redirect signed-out users to the upgrade page (which has its own sign-in
  // prompt). We wait for auth to resolve before redirecting to avoid a flash.
  useEffect(() => {
    if (loading) return;
    setAuthChecked(true);
    if (!email) {
      navigate("/upgrade", { replace: true });
    }
  }, [email, loading, navigate]);

  const cancelsAtPeriodEnd = useMemo(() => {
    // We can't read cancel_at_period_end directly (not on SubscriptionStatus),
    // but Stripe sets status='active' with current_period_end in the future
    // until the period actually ends. The unambiguous signal we DO have is
    // status='canceled' + future current_period_end → end-of-period grace.
    return (
      subscription?.status === "canceled" &&
      subscription?.current_period_end &&
      new Date(subscription.current_period_end).getTime() > Date.now()
    );
  }, [subscription]);

  const statusBadge = (() => {
    if (!subscription || !isActive) {
      return { label: "Free plan", tone: "muted" as const };
    }
    if (cancelsAtPeriodEnd) {
      return { label: "Cancels soon", tone: "warning" as const };
    }
    if (inTrial) {
      return {
        label: `Trial · ${daysLeftInTrial} day${daysLeftInTrial === 1 ? "" : "s"} left`,
        tone: "info" as const,
      };
    }
    return { label: "Active", tone: "success" as const };
  })();

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error("Please sign in again to open billing.");
        return;
      }
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/account`,
        },
      });
      if (error || !data?.url) {
        toast.error(data?.error || error?.message || "Couldn't open billing portal.");
        return;
      }
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't open billing portal.");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: { environment: getStripeEnvironment() },
      });
      if (error || !data?.ok) {
        toast.error(data?.error || error?.message || "Couldn't cancel subscription.");
        return;
      }
      toast.success("Cancellation scheduled", {
        description: data.current_period_end
          ? `You'll keep access until ${formatDate(data.current_period_end)}.`
          : "You'll keep access until the end of the current period.",
      });
      setCancelOpen(false);
      // Give the webhook a beat then refresh; the optimistic update on the
      // server side already wrote current_period_end, but status=canceled
      // is set by the webhook.
      await new Promise((r) => setTimeout(r, 1500));
      await refresh();
    } catch (err) {
      console.error(err);
      toast.error("Couldn't cancel subscription.");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  const handleDelete = async () => {
    if (deleteConfirm.trim().toUpperCase() !== "DELETE") return;
    setDeleteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { environment: getStripeEnvironment() },
      });
      if (error || !data?.ok) {
        toast.error(data?.error || error?.message || "Couldn't delete account.");
        setDeleteLoading(false);
        return;
      }
      toast.success("Account deleted");
      // The auth user is gone; sign out locally to clear cached session.
      await supabase.auth.signOut();
      navigate("/", { replace: true });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't delete account.");
      setDeleteLoading(false);
    }
  };

  if (loading || !authChecked) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="mx-auto flex max-w-2xl items-center justify-center px-6 py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  const isFree = !isActive;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-2xl px-6 pb-16">
        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-foreground">
            Your account
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{email}</p>
        </header>

        {/* Plan & billing */}
        <section className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-lg font-semibold">Plan</h2>
                <span
                  className={
                    statusBadge.tone === "success"
                      ? "rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary"
                      : statusBadge.tone === "info"
                        ? "rounded-full bg-secondary/30 px-2 py-0.5 text-xs font-medium text-foreground"
                        : statusBadge.tone === "warning"
                          ? "rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                          : "rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                  }
                >
                  {statusBadge.label}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {isFree
                  ? "You're on the free plan — up to 5 saved recipes."
                  : planLabel(subscription?.price_id ?? null)}
              </p>
              {isActive && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {cancelsAtPeriodEnd
                    ? `Access ends ${formatDate(subscription?.current_period_end ?? null)}.`
                    : inTrial
                      ? `Trial ends ${formatDate(subscription?.trial_end ?? null)} — your card is charged then.`
                      : `Renews ${formatDate(subscription?.current_period_end ?? null)}.`}
                </p>
              )}
            </div>
          </div>

          {isFree ? (
            <Button
              className="mt-4 w-full rounded-full"
              onClick={() => navigate("/upgrade")}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Start your 14-day free trial
            </Button>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              {inTrial && !cancelsAtPeriodEnd && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setSwitchOpen(true)}
                >
                  Switch plan
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={openPortal}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Invoices &amp; payment method
                <ExternalLink className="ml-1 h-3 w-3 opacity-60" />
              </Button>
              {!cancelsAtPeriodEnd && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-muted-foreground hover:text-foreground"
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel subscription
                </Button>
              )}
            </div>
          )}
        </section>

        {/* Live payment verification — owner-only dev/QA tool. */}
        {email && isProjectOwner(email) && <PaymentVerificationPanel email={email} />}

        {/* Units — available to all tiers (free & paid). */}
        <UnitsSection />

        {/* Diet preference — available to all tiers. Drives auto-tagging when cleaning recipes. */}
        <DietSection />

        {email && <DefaultMealsSection email={email} />}

        <section className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h2 className="font-display text-lg font-semibold">Session</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Signed in as <span className="text-foreground">{email}</span>
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 rounded-full"
            onClick={handleSignOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </section>

        {/* Danger zone */}
        <section className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-lg font-semibold">Delete account</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Permanently remove your recipes, menus, & shopping lists. Any active
                subscription will be cancelled immediately. Billing history is retained
                for our records.
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="mt-4 rounded-full"
                onClick={() => {
                  setDeleteConfirm("");
                  setDeleteOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete my account
              </Button>
            </div>
          </div>
        </section>

        <p className="mt-8 flex items-center justify-center gap-1 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" />
          Payments are processed securely by Stripe.
        </p>
      </main>

      {/* Cancel confirm */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll keep full access until{" "}
              <span className="font-medium text-foreground">
                {formatDate(subscription?.current_period_end ?? subscription?.trial_end ?? null)}
              </span>
              . After that, you'll drop to the free plan (5 saved recipes max).
              Your saved recipes stay safe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelLoading}>Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleCancel();
              }}
              disabled={cancelLoading}
            >
              {cancelLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling…
                </>
              ) : (
                "Cancel subscription"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Switch plan sheet */}
      <Sheet open={switchOpen} onOpenChange={setSwitchOpen}>
        <SheetContent side="right" className="w-full max-w-xl overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Switch your plan</SheetTitle>
            <SheetDescription>
              Pick a different plan or billing interval. Your trial is preserved — you
              won't be charged until it ends.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {switchOpen && (
              <UpsellContent
                showCheckout={true}
                email={email ?? ""}
                returnUrl={`${window.location.origin}/account?switched=1`}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes all your saved recipes, menus & shopping lists, cancels any
              active subscription, & signs you out. <strong>This can't be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-confirm" className="text-sm">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              disabled={deleteLoading}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Keep account</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleteLoading || deleteConfirm.trim().toUpperCase() !== "DELETE"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete forever"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const UnitsSection = () => {
  const { prefs, setPref } = usePreferences();
  const options: { value: UnitSystem; label: string; hint: string }[] = [
    { value: "auto", label: "Auto", hint: "Match your device locale" },
    { value: "metric", label: "Metric", hint: "g, ml, °C, cm" },
    { value: "imperial", label: "Imperial", hint: "oz/lb, fl oz/cup, °F, in" },
  ];
  return (
    <section className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
      <h2 className="font-display text-lg font-semibold">Units</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Show recipes in metric or imperial. Conversions are applied on the fly —
        the original recipe isn't changed. Available on every plan.
      </p>
      <div
        role="radiogroup"
        aria-label="Measurement system"
        className="mt-4 grid gap-2 sm:grid-cols-3"
      >
        {options.map((opt) => {
          const active = prefs.units === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setPref("units", opt.value)}
              className={
                "flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-smooth " +
                (active
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground")
              }
            >
              <span className="text-sm font-semibold text-foreground">{opt.label}</span>
              <span className="text-xs">{opt.hint}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const DIET_PRESETS: { value: DietPreference; label: string; hint: string }[] = [
  { value: { kind: "none" }, label: "None", hint: "No auto-tagging" },
  { value: { kind: "preset", value: "vegan" }, label: "Vegan", hint: "No animal products" },
  { value: { kind: "preset", value: "vegetarian" }, label: "Vegetarian", hint: "No meat or fish" },
  { value: { kind: "preset", value: "gluten-free" }, label: "Gluten-free", hint: "No wheat, barley, rye" },
];

const DietSection = () => {
  const { prefs, setPref } = usePreferences();
  const isCustom = prefs.diet.kind === "custom";
  const [showCustomInput, setShowCustomInput] = useState(isCustom);
  const [customDraft, setCustomDraft] = useState(prefs.diet.kind === "custom" ? prefs.diet.label : "");

  const matchesPreset = (a: DietPreference, b: DietPreference): boolean => {
    if (a.kind !== b.kind) return false;
    if (a.kind === "none" || b.kind === "none") return a.kind === b.kind;
    if (a.kind === "preset" && b.kind === "preset") return a.value === b.value;
    return false;
  };

  const handlePresetClick = (value: DietPreference) => {
    setShowCustomInput(false);
    setPref("diet", value);
  };

  const commitCustom = () => {
    const trimmed = customDraft.trim().slice(0, 40);
    if (!trimmed) {
      // Empty → revert to none.
      setPref("diet", { kind: "none" });
      return;
    }
    setPref("diet", { kind: "custom", label: trimmed });
  };

  return (
    <section className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
      <h2 className="font-display text-lg font-semibold">Diet preference</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        When set, we'll tag recipes that match your diet — only when we're confident.
        You can always edit or remove tags. Available on every plan.
      </p>
      <div role="radiogroup" aria-label="Diet preference" className="mt-4 grid gap-2 sm:grid-cols-2">
        {DIET_PRESETS.map((opt) => {
          const active = !isCustom && matchesPreset(prefs.diet, opt.value);
          return (
            <button
              key={opt.label}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handlePresetClick(opt.value)}
              className={
                "flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-smooth " +
                (active
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground")
              }
            >
              <span className="text-sm font-semibold text-foreground">{opt.label}</span>
              <span className="text-xs">{opt.hint}</span>
            </button>
          );
        })}
        <button
          type="button"
          role="radio"
          aria-checked={isCustom || showCustomInput}
          onClick={() => setShowCustomInput(true)}
          className={
            "flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-smooth sm:col-span-2 " +
            (isCustom
              ? "border-primary bg-primary/5 text-foreground"
              : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground")
          }
        >
          <span className="text-sm font-semibold text-foreground">
            {isCustom ? `Custom · ${dietLabel(prefs.diet) ?? ""}` : "Custom…"}
          </span>
          <span className="text-xs">e.g. Pescatarian, Dairy-free, Low FODMAP, Halal</span>
        </button>
      </div>
      {showCustomInput && (
        <div className="mt-3 flex gap-2">
          <Input
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            onBlur={commitCustom}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitCustom();
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="Your diet (max 40 chars)"
            maxLength={40}
            aria-label="Custom diet preference"
          />
          <Button type="button" variant="outline" size="sm" onClick={commitCustom}>
            Save
          </Button>
        </div>
      )}
    </section>
  );
};

const DefaultMealsSection = ({ email }: { email: string }) => {
  const [slots, setSlots] = useState<MealSlot[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getUserShoppingPrefs(email).then((p) => {
      if (!cancelled) setSlots(p.defaultMealSlots);
    });
    return () => {
      cancelled = true;
    };
  }, [email]);

  const toggle = async (id: MealSlot) => {
    if (!slots) return;
    const has = slots.includes(id);
    if (has && slots.length <= 1) return; // keep at least one
    const next = has
      ? slots.filter((s) => s !== id)
      : MEAL_SLOTS.filter((s) => slots.includes(s.id) || s.id === id).map((s) => s.id);
    setSlots(next);
    setSaving(true);
    try {
      await saveDefaultMealSlots(email, next);
    } catch {
      toast.error("Couldn't save default meals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
      <h2 className="font-display text-lg font-semibold">Default meals for new plans</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Pick which meal slots show up by default when you start a new weekly plan. You
        can still adjust each plan individually.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {MEAL_SLOTS.map((meal) => {
          const active = slots?.includes(meal.id) ?? false;
          const isLastActive = active && (slots?.length ?? 0) <= 1;
          return (
            <button
              key={meal.id}
              type="button"
              onClick={() => void toggle(meal.id)}
              aria-pressed={active}
              disabled={!slots || saving || isLastActive}
              className={
                "rounded-full border px-3 py-1.5 text-xs font-medium transition-smooth " +
                (active
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground") +
                (isLastActive ? " cursor-not-allowed opacity-80" : "")
              }
            >
              {meal.label}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default Account;
